import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Shield, Key, FileWarning, Download, RefreshCw, RotateCcw,
  ShieldAlert, ShieldCheck, Fingerprint, Timer, Bug, CheckCircle2,
  Loader2, XCircle, Trash2, Plus, Link2,
} from "lucide-react";
import { Link } from "react-router-dom";

// ---------- helpers ---------------------------------------------------------

const STATUS_TONE = {
  healthy: { bg: "#DCFCE7", fg: "#166534", label: "Healthy" },
  warning: { bg: "#FEF3C7", fg: "#92400E", label: "Warning" },
  critical: { bg: "#FEE2E2", fg: "#991B1B", label: "Critical" },
  expired: { bg: "#FEE2E2", fg: "#991B1B", label: "Expired" },
  missing: { bg: "#F1F5F9", fg: "#475569", label: "Not configured" },
};

const SEV_TONE = {
  critical: { bg: "#FEE2E2", fg: "#991B1B", dot: "#DC2626" },
  high: { bg: "#FEF3C7", fg: "#92400E", dot: "#F59E0B" },
  medium: { bg: "#DBEAFE", fg: "#1E40AF", dot: "#3B82F6" },
  low: { bg: "#DCFCE7", fg: "#166534", dot: "#22C55E" },
  informational: { bg: "#F1F5F9", fg: "#475569", dot: "#64748B" },
};

function Badge({ tone, children, testid }) {
  const t = STATUS_TONE[tone] || STATUS_TONE.missing;
  return (
    <span
      data-testid={testid}
      style={{ background: t.bg, color: t.fg }}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium uppercase tracking-widest"
    >
      {children ?? t.label}
    </span>
  );
}

function SevBadge({ severity, testid }) {
  const t = SEV_TONE[severity] || SEV_TONE.informational;
  return (
    <span
      data-testid={testid}
      style={{ background: t.bg, color: t.fg }}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium uppercase tracking-widest"
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.dot }} />
      {severity}
    </span>
  );
}

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

// ---------- SAML SP Cert card ----------------------------------------------

function SamlSpCard({ data, onRotated }) {
  const [busy, setBusy] = useState(false);
  const rotate = async () => {
    if (!window.confirm("Rotate the SAML SP signing certificate?\n\nYou must re-upload the new SP metadata to every IdP within 24 hours.")) return;
    setBusy(true);
    try {
      const r = await api.post("/security-lifecycle/saml-sp/rotate", {
        validity_years: 10, key_size: 3072,
      });
      toast.success(`SAML SP cert rotated. Fingerprint ${r.data.certificate.fingerprint_sha256.slice(0, 40)}…`);
      onRotated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const download = () => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const t = localStorage.getItem("hw_access_token") || "";
    // Trigger download via authenticated fetch (avoid new tab losing token)
    fetch(`${base}/api/security-lifecycle/saml-sp/certificate.pem`, {
      headers: { Authorization: `Bearer ${t}` },
    })
      .then((res) => res.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "harborwell-saml-sp.pem";
        a.click(); URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Failed to download certificate"));
  };
  const cert = data;
  const tone = cert.status || (cert.present ? "healthy" : "missing");
  return (
    <div className="rounded-2xl border border-[#E5E5E0] bg-white p-6" data-testid="saml-sp-card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Shield className="w-4 h-4 text-[#1E3A8A]" />
            <div className="text-[10px] uppercase tracking-widest text-[#7E937D]">SAML Service Provider</div>
          </div>
          <h2 className="text-lg font-semibold text-[#1E3A8A]">Signing Certificate</h2>
        </div>
        <Badge tone={tone} testid="saml-sp-status" />
      </div>
      {cert.present ? (
        <div className="space-y-2 text-sm">
          <div className="grid grid-cols-2 gap-x-6 gap-y-1">
            <Row label="Subject" value={cert.subject} />
            <Row label="Issuer" value={cert.issuer} />
            <Row label="Not before" value={fmt(cert.not_before)} />
            <Row label="Not after" value={fmt(cert.not_after)} testid="saml-sp-not-after" />
            <Row label="Days until expiry" value={cert.days_until_expiry} testid="saml-sp-days-left" />
            <Row label="Self-signed" value={cert.self_signed ? "yes" : "no"} />
          </div>
          <div className="pt-2 border-t border-[#F1F5F9] mt-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Fingerprint (SHA-256)</div>
            <code data-testid="saml-sp-fingerprint" className="text-[11px] font-mono break-all block bg-[#F9F8F6] p-2 rounded">
              {cert.fingerprint_sha256}
            </code>
          </div>
        </div>
      ) : (
        <div className="text-sm text-slate-500" data-testid="saml-sp-empty">
          No SAML SP certificate is configured. Rotate to generate a fresh self-signed cert; the private key
          stays on this backend and only the public certificate needs to be uploaded to your IdP.
        </div>
      )}
      <div className="flex items-center gap-2 mt-5">
        <button
          onClick={rotate} disabled={busy}
          data-testid="saml-sp-rotate-btn"
          className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-sm bg-[#1E3A8A] text-white hover:opacity-90 disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          {cert.present ? "Rotate certificate" : "Generate certificate"}
        </button>
        {cert.present && (
          <button
            onClick={download} data-testid="saml-sp-download-btn"
            className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-sm border border-[#E5E5E0] hover:bg-slate-50"
          >
            <Download className="w-4 h-4" /> Download PEM
          </button>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-500 text-xs uppercase tracking-wider">{label}</span>
      <span data-testid={testid} className="font-medium text-right">{value ?? "—"}</span>
    </div>
  );
}

// ---------- JWT rotation card ----------------------------------------------

function JwtCard({ data, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [grace, setGrace] = useState(60);
  const rotate = async () => {
    if (!window.confirm(`Rotate the JWT signing key with a ${grace}-minute grace window?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/security-lifecycle/jwt/rotate", {
        grace_window_minutes: grace, reason: "operator initiated",
      });
      toast.success(`JWT rotated → ${r.data.rotation.new_fingerprint}`);
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const rollback = async () => {
    if (!window.confirm("Rollback to the previous JWT key? Existing tokens will invalidate.")) return;
    setBusy(true);
    try {
      const r = await api.post("/security-lifecycle/jwt/rollback");
      toast.success(`Rolled back to ${r.data.rotation.new_fingerprint}`);
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const gracePct = (() => {
    if (!data.previous_grace_seconds_remaining) return 0;
    // Best-effort — we don't know the original window so cap at 60m visually.
    return Math.min(100, (data.previous_grace_seconds_remaining / 3600) * 100);
  })();
  return (
    <div className="rounded-2xl border border-[#E5E5E0] bg-white p-6" data-testid="jwt-card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Key className="w-4 h-4 text-[#1E3A8A]" />
            <div className="text-[10px] uppercase tracking-widest text-[#7E937D]">Session Signing</div>
          </div>
          <h2 className="text-lg font-semibold text-[#1E3A8A]">JWT Rotation</h2>
        </div>
        <Badge tone={data.previous_active ? "warning" : "healthy"} testid="jwt-status">
          {data.previous_active ? "Grace window open" : "Single key"}
        </Badge>
      </div>
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Current key</div>
          <code data-testid="jwt-current-fp" className="text-[11px] font-mono block bg-[#F0FDF4] text-[#166534] p-2 rounded">
            {data.current_fingerprint}
          </code>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Previous key</div>
          <code data-testid="jwt-previous-fp" className="text-[11px] font-mono block bg-[#F9F8F6] text-slate-600 p-2 rounded">
            {data.previous_fingerprint || "— (not in grace window)"}
          </code>
        </div>
      </div>
      {data.previous_active && (
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-slate-500 flex items-center gap-1"><Timer className="w-3 h-3" /> Previous key active for</span>
            <span data-testid="jwt-grace-remaining" className="font-medium">
              {Math.max(0, Math.floor((data.previous_grace_seconds_remaining || 0) / 60))} min remaining
            </span>
          </div>
          <div className="h-1.5 bg-[#F1F5F9] rounded-full overflow-hidden">
            <div className="h-full bg-[#F59E0B] transition-all" style={{ width: `${gracePct}%` }} />
          </div>
        </div>
      )}
      <div className="flex items-center gap-3 mt-5">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span>Grace window:</span>
          <select
            value={grace} data-testid="jwt-grace-select"
            onChange={(e) => setGrace(parseInt(e.target.value, 10))}
            className="border border-[#E5E5E0] rounded-lg px-2 py-1 text-xs"
          >
            <option value={0}>Immediate</option>
            <option value={15}>15 minutes</option>
            <option value={60}>1 hour</option>
            <option value={480}>8 hours</option>
            <option value={1440}>24 hours</option>
          </select>
        </div>
        <button
          onClick={rotate} disabled={busy}
          data-testid="jwt-rotate-btn"
          className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-sm bg-[#1E3A8A] text-white hover:opacity-90 disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Rotate JWT
        </button>
        {data.previous_active && (
          <button
            onClick={rollback} disabled={busy}
            data-testid="jwt-rollback-btn"
            className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-sm border border-[#FCA5A5] text-[#991B1B] hover:bg-[#FEF2F2]"
          >
            <RotateCcw className="w-4 h-4" /> Rollback
          </button>
        )}
      </div>
      {data.last_rotation && (
        <div className="mt-4 pt-3 border-t border-[#F1F5F9] text-xs text-slate-500">
          Last rotated {fmt(data.last_rotation.rotated_at)} by
          <span className="font-mono ml-1">{data.last_rotation.actor_id?.slice(0, 12) || "unknown"}</span>
        </div>
      )}
    </div>
  );
}

// ---------- SCIM rotation card ---------------------------------------------

function ScimCard({ tenants, onRotated }) {
  const [rotating, setRotating] = useState(null);
  const [revealed, setRevealed] = useState(null);
  const rotate = async (slug) => {
    if (!window.confirm(`Rotate SCIM bearer token for tenant '${slug}'?\n\nThe new token is shown ONCE. Copy it to the IdP immediately.`)) return;
    setRotating(slug);
    try {
      const r = await api.post(`/security-lifecycle/scim/tenants/${slug}/rotate-token`, {
        reason: "operator initiated",
      });
      setRevealed({ slug, token: r.data.bearer_token });
      toast.success(`SCIM token rotated for ${slug}`);
      onRotated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRotating(null); }
  };
  return (
    <div className="rounded-2xl border border-[#E5E5E0] bg-white p-6" data-testid="scim-card">
      <div className="flex items-center gap-2 mb-4">
        <Fingerprint className="w-4 h-4 text-[#1E3A8A]" />
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#7E937D]">SCIM Provisioning</div>
          <h2 className="text-lg font-semibold text-[#1E3A8A]">Tenant Bearer Tokens</h2>
        </div>
      </div>
      {tenants.length === 0 && (
        <div className="text-sm text-slate-500 py-4 text-center" data-testid="scim-empty">
          No SCIM-enabled tenants configured.
        </div>
      )}
      {tenants.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-widest text-slate-500 border-b border-[#F1F5F9]">
            <tr>
              <th className="text-left py-2">Tenant</th>
              <th className="text-left py-2">Enabled</th>
              <th className="text-left py-2">Last rotated</th>
              <th className="text-right py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F1F5F9]">
            {tenants.map((t) => (
              <tr key={t.slug} data-testid={`scim-row-${t.slug}`}>
                <td className="py-2.5">
                  <div className="font-medium">{t.display_name}</div>
                  <div className="text-xs text-slate-400 font-mono">{t.slug}</div>
                </td>
                <td className="py-2.5">
                  {t.enabled
                    ? <Badge tone="healthy">SCIM on</Badge>
                    : <Badge tone="missing">Off</Badge>}
                </td>
                <td className="py-2.5 text-xs text-slate-500">{fmt(t.last_rotated_at)}</td>
                <td className="py-2.5 text-right">
                  <button
                    onClick={() => rotate(t.slug)}
                    disabled={!t.enabled || rotating === t.slug}
                    data-testid={`scim-rotate-${t.slug}`}
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-[#E5E5E0] rounded-full hover:bg-slate-50 disabled:opacity-40"
                  >
                    {rotating === t.slug ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    Rotate
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {revealed && (
        <div className="mt-5 border border-[#FCA5A5] bg-[#FEF2F2] rounded-xl p-4" data-testid="scim-revealed">
          <div className="flex items-start justify-between mb-2">
            <div className="font-medium text-[#991B1B] text-sm">
              New SCIM bearer token for {revealed.slug}
            </div>
            <button
              className="text-xs text-slate-500 hover:text-slate-700"
              onClick={() => setRevealed(null)} data-testid="scim-revealed-close"
            >Dismiss</button>
          </div>
          <div className="text-xs text-[#991B1B] mb-2">
            This is the ONLY time you will see this token. Copy it to the IdP now.
          </div>
          <code className="block bg-white p-2 rounded text-xs font-mono break-all border border-[#FEE2E2]"
                data-testid="scim-revealed-token">
            {revealed.token}
          </code>
          <button
            onClick={() => { navigator.clipboard.writeText(revealed.token); toast.success("Copied to clipboard"); }}
            className="mt-2 text-xs px-2 py-1 bg-[#991B1B] text-white rounded-full"
          >Copy to clipboard</button>
        </div>
      )}
    </div>
  );
}

// ---------- Audit-chain card ------------------------------------------------

function AuditChainCard({ data, onChanged }) {
  const [busy, setBusy] = useState(null);
  const [result, setResult] = useState(null);
  const call = async (action, path, method = "POST") => {
    setBusy(action); setResult(null);
    try {
      const r = method === "GET" ? await api.get(path) : await api.post(path);
      setResult({ action, data: r.data });
      toast.success(`${action} → ${r.data.status || "ok"}`);
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };
  return (
    <div className="rounded-2xl border border-[#E5E5E0] bg-white p-6" data-testid="audit-chain-card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Link2 className="w-4 h-4 text-[#1E3A8A]" />
            <div className="text-[10px] uppercase tracking-widest text-[#7E937D]">Tamper Detection</div>
          </div>
          <h2 className="text-lg font-semibold text-[#1E3A8A]">Audit Log Integrity Chain</h2>
        </div>
        <Badge tone={data.coverage_pct >= 99 ? "healthy" : data.coverage_pct >= 90 ? "warning" : "critical"}
               testid="chain-coverage-badge">
          {data.coverage_pct}% coverage
        </Badge>
      </div>
      <div className="grid grid-cols-3 gap-4 mb-4">
        <Kpi label="Total rows" value={data.total_audit_rows} testid="chain-total-rows" />
        <Kpi label="Chained" value={data.chained_rows} testid="chain-chained-rows" />
        <Kpi label="Head index" value={data.head_index ?? "—"} testid="chain-head-index" />
      </div>
      {data.head_chain_hash && (
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Head chain hash</div>
          <code data-testid="chain-head-hash" className="text-[11px] font-mono block bg-[#F9F8F6] p-2 rounded break-all">
            {data.head_chain_hash}
          </code>
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => call("Verify", "/security-lifecycle/audit-chain/verify")}
                disabled={busy}
                data-testid="chain-verify-btn"
                className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-sm bg-[#1E3A8A] text-white hover:opacity-90 disabled:opacity-50">
          {busy === "Verify" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
          Verify chain
        </button>
        <button onClick={() => call("Backfill", "/security-lifecycle/audit-chain/backfill")}
                disabled={busy}
                data-testid="chain-backfill-btn"
                className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-sm border border-[#E5E5E0] hover:bg-slate-50">
          {busy === "Backfill" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Backfill missing rows
        </button>
        <button onClick={() => call("Snapshot", "/security-lifecycle/audit-chain/snapshot-root")}
                disabled={busy}
                data-testid="chain-snapshot-btn"
                className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-sm border border-[#E5E5E0] hover:bg-slate-50">
          {busy === "Snapshot" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Fingerprint className="w-4 h-4" />}
          Snapshot daily root
        </button>
      </div>
      {result && (
        <div className="mt-4 border-t border-[#F1F5F9] pt-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Last {result.action}</div>
          <pre data-testid="chain-last-result"
               className="text-[11px] font-mono bg-[#F9F8F6] p-2 rounded overflow-auto max-h-40">
            {JSON.stringify(result.data, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, testid }) {
  return (
    <div className="rounded-xl border border-[#F1F5F9] bg-[#F9F8F6] p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">{label}</div>
      <div className="text-lg font-semibold text-[#1E3A8A]">{value}</div>
    </div>
  );
}

// ---------- Pen-test findings card -----------------------------------------

function PentestCard({ counts, onChanged }) {
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({
    title: "", description: "", severity: "medium", affected_component: "",
    engagement_ref: "", cvss_score: "",
  });
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/security-lifecycle/pentest/findings?limit=100");
      setFindings(r.data.findings || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const submit = async () => {
    if (!draft.title || !draft.description) {
      toast.error("Title and description are required");
      return;
    }
    setCreating(true);
    try {
      const payload = { ...draft };
      if (!payload.cvss_score) delete payload.cvss_score;
      else payload.cvss_score = parseFloat(payload.cvss_score);
      await api.post("/security-lifecycle/pentest/findings", payload);
      setDraft({ title: "", description: "", severity: "medium", affected_component: "", engagement_ref: "", cvss_score: "" });
      toast.success("Finding logged");
      await load();
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreating(false); }
  };
  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/security-lifecycle/pentest/findings/${id}`, { status });
      toast.success(`Marked ${status}`);
      await load();
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this pen-test finding? This action is audit-logged.")) return;
    try {
      await api.delete(`/security-lifecycle/pentest/findings/${id}`);
      await load();
      onChanged();
      toast.success("Finding deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="rounded-2xl border border-[#E5E5E0] bg-white p-6" data-testid="pentest-card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Bug className="w-4 h-4 text-[#1E3A8A]" />
            <div className="text-[10px] uppercase tracking-widest text-[#7E937D]">External Security Validation</div>
          </div>
          <h2 className="text-lg font-semibold text-[#1E3A8A]">Pen-Test Findings</h2>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {["critical", "high", "medium", "low"].map((sev) => (
            <div key={sev} className="flex items-center gap-1" data-testid={`pentest-count-${sev}`}>
              <SevBadge severity={sev} /> <span className="font-semibold">{counts[sev] || 0}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Create form */}
      <div className="grid grid-cols-2 gap-3 mb-4 p-4 border border-[#F1F5F9] rounded-xl bg-[#F9F8F6]">
        <input
          data-testid="pentest-title"
          className="px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm col-span-2"
          placeholder="Finding title (e.g. 'Reflected XSS in /api/foo')"
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
        />
        <textarea
          data-testid="pentest-description"
          className="px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm col-span-2 min-h-[70px]"
          placeholder="Description + reproduction steps"
          value={draft.description}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
        />
        <select
          data-testid="pentest-severity"
          value={draft.severity}
          onChange={(e) => setDraft({ ...draft, severity: e.target.value })}
          className="px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm"
        >
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
          <option value="informational">Informational</option>
        </select>
        <input
          data-testid="pentest-component"
          className="px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm"
          placeholder="Affected component"
          value={draft.affected_component}
          onChange={(e) => setDraft({ ...draft, affected_component: e.target.value })}
        />
        <input
          data-testid="pentest-cvss"
          className="px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm"
          placeholder="CVSS (0.0-10.0)" type="number" step="0.1" min="0" max="10"
          value={draft.cvss_score}
          onChange={(e) => setDraft({ ...draft, cvss_score: e.target.value })}
        />
        <input
          data-testid="pentest-engagement"
          className="px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm"
          placeholder="Engagement / vendor ref"
          value={draft.engagement_ref}
          onChange={(e) => setDraft({ ...draft, engagement_ref: e.target.value })}
        />
        <button
          onClick={submit} disabled={creating}
          data-testid="pentest-create-btn"
          className="col-span-2 inline-flex items-center justify-center gap-1 px-3 py-2 rounded-full text-sm bg-[#1E3A8A] text-white hover:opacity-90 disabled:opacity-50"
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Log finding
        </button>
      </div>

      {/* Findings list */}
      {loading && <div className="text-sm text-slate-400 text-center py-4">Loading…</div>}
      {!loading && findings.length === 0 && (
        <div className="text-sm text-slate-500 text-center py-4" data-testid="pentest-empty">
          No pen-test findings recorded yet.
        </div>
      )}
      <ul className="divide-y divide-[#F1F5F9]">
        {findings.map((f) => (
          <li key={f.id} className="py-3" data-testid={`pentest-item-${f.id.slice(0, 8)}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <SevBadge severity={f.severity} />
                  <span className="font-medium truncate">{f.title}</span>
                  <Badge
                    tone={f.status === "remediated" ? "healthy"
                      : f.status === "open" ? "critical"
                      : f.status === "in_progress" ? "warning" : "missing"}
                  >
                    {f.status.replace("_", " ")}
                  </Badge>
                </div>
                <div className="text-xs text-slate-500 truncate max-w-xl">{f.description}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">
                  {f.affected_component && <>Component: <code>{f.affected_component}</code> · </>}
                  Logged {fmt(f.created_at)} by <code>{f.created_by_email || "unknown"}</code>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {f.status !== "in_progress" && (
                  <button onClick={() => changeStatus(f.id, "in_progress")}
                          className="text-xs px-2 py-1 border border-[#E5E5E0] rounded-full hover:bg-slate-50"
                          data-testid={`pentest-inprogress-${f.id.slice(0, 8)}`}>
                    In progress
                  </button>
                )}
                {f.status !== "remediated" && (
                  <button onClick={() => changeStatus(f.id, "remediated")}
                          className="text-xs px-2 py-1 bg-[#DCFCE7] text-[#166534] rounded-full hover:opacity-80"
                          data-testid={`pentest-remediate-${f.id.slice(0, 8)}`}>
                    Mark remediated
                  </button>
                )}
                <button onClick={() => del(f.id)}
                        className="text-xs p-1.5 border border-[#FEE2E2] text-[#991B1B] rounded-full hover:bg-[#FEF2F2]"
                        data-testid={`pentest-delete-${f.id.slice(0, 8)}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------- Cert-expiry alerts card -----------------------------------------

function CertAlertsCard({ onScan }) {
  const [alerts, setAlerts] = useState([]);
  const [scanning, setScanning] = useState(false);
  const load = async () => {
    try {
      const r = await api.get("/security-lifecycle/certificates/alerts");
      setAlerts(r.data.alerts || []);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);
  const scan = async () => {
    setScanning(true);
    try {
      const r = await api.post("/security-lifecycle/certificates/scan");
      toast.success(`Scanned ${r.data.total_checked} certs — ${r.data.warnings.length} warning(s)`);
      await load();
      onScan();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setScanning(false); }
  };
  return (
    <div className="rounded-2xl border border-[#E5E5E0] bg-white p-6" data-testid="cert-alerts-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FileWarning className="w-4 h-4 text-[#1E3A8A]" />
          <h2 className="text-lg font-semibold text-[#1E3A8A]">Certificate Expiry Monitor</h2>
        </div>
        <button
          onClick={scan} disabled={scanning}
          data-testid="cert-scan-btn"
          className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-sm border border-[#E5E5E0] hover:bg-slate-50"
        >
          {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Run scan
        </button>
      </div>
      {alerts.length === 0 ? (
        <div className="text-sm py-4 flex items-center gap-2 text-[#166534] justify-center" data-testid="cert-alerts-empty">
          <CheckCircle2 className="w-4 h-4" /> All monitored certificates are healthy.
        </div>
      ) : (
        <ul className="space-y-2">
          {alerts.map((a, i) => (
            <li key={i} className="flex items-center justify-between p-3 rounded-lg border border-[#FEF3C7] bg-[#FFFBEB]"
                data-testid={`cert-alert-${i}`}>
              <div>
                <div className="font-medium text-sm">{a.identifier}</div>
                <div className="text-xs text-slate-500 font-mono">{a.source}</div>
              </div>
              <div className="text-right">
                <div className={`text-sm font-semibold ${a.days_until_expiry < 0 ? "text-[#991B1B]" : "text-[#92400E]"}`}>
                  {a.days_until_expiry < 0
                    ? `Expired ${Math.abs(a.days_until_expiry)}d ago`
                    : `Expires in ${a.days_until_expiry}d`}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">{a.status}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------- main page ------------------------------------------------------

export default function SecurityLifecycle() {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get("/security-lifecycle/summary");
      setSummary(r.data);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-7xl mx-auto p-8 space-y-6" data-testid="security-lifecycle-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="hw-eyebrow text-[#F59E0B] mb-1 flex items-center gap-2">
            <ShieldAlert className="w-3.5 h-3.5" />
            Production Security
          </div>
          <h1 className="font-display text-3xl font-semibold text-[#1E3A8A]">
            Security Lifecycle
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Certificates, signing keys, tamper-detection, and pen-test evidence — all in one operator console.
            Every action here is audit-logged and appended to the integrity chain.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/identity-security" data-testid="link-identity-security"
                className="text-xs text-[#1E3A8A] hover:underline">
            → Identity Governance
          </Link>
          <button onClick={load}
                  data-testid="lifecycle-refresh-btn"
                  className="p-2 rounded-full border border-[#E5E5E0] bg-white hover:bg-slate-50">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {loading && !summary && (
        <div className="text-center py-10 text-slate-500" data-testid="lifecycle-loading">
          <Loader2 className="w-6 h-6 animate-spin inline-block mr-2" />
          Loading security lifecycle telemetry…
        </div>
      )}

      {error && !summary && (
        <div className="rounded-2xl border border-[#FCA5A5] bg-[#FEF2F2] p-6 flex items-start gap-3"
             data-testid="lifecycle-error">
          <XCircle className="w-6 h-6 text-[#991B1B] flex-shrink-0" />
          <div>
            <div className="font-semibold text-[#991B1B]">Failed to load</div>
            <div className="text-sm text-[#991B1B] mt-1">{error}</div>
          </div>
        </div>
      )}

      {summary && (
        <div className="space-y-6" data-testid="lifecycle-content">
          {loading && (
            <div className="text-xs text-slate-400 flex items-center gap-1"
                 data-testid="lifecycle-inline-refreshing">
              <Loader2 className="w-3 h-3 animate-spin" /> Refreshing telemetry…
            </div>
          )}
          <div className="grid lg:grid-cols-2 gap-6">
            <SamlSpCard data={summary.saml_sp_certificate} onRotated={load} />
            <JwtCard data={summary.jwt} onChanged={load} />
          </div>
          <CertAlertsCard onScan={load} />
          <div className="grid lg:grid-cols-2 gap-6">
            <ScimCard tenants={summary.scim_tenants} onRotated={load} />
            <AuditChainCard data={summary.audit_chain} onChanged={load} />
          </div>
          <PentestCard counts={summary.pentest_findings.by_severity_active} onChanged={load} />
        </div>
      )}
    </div>
  );
}
